import React from 'react'
import Select, { type Options, type SingleValue } from 'react-select'
import { useNavigate } from 'react-router-dom'
import FieldLabel from '../input/FieldLabel'

type Option = { label: string, value: string }
type Props = {
  label?: string
  size?: 'md' | 'sm' | 'lg'
  onChange: (e: any) => void
  options: Option[]
  defaultValue?: string | string[]
  isMulti?: boolean
  id?: string
  name: string
  placeHolder?: string
  disabled?: boolean
  required?: boolean
  noBorder?: boolean
  isClearable?: boolean
  [x:string]: any
}

const SelectBox: React.FC<Props> = ({ placeHolder, name, id, size='md', label, onChange, options, isMulti, value, defaultValue, disabled, required, noBorder, isClearable, ...rest }) => {
  const navigate = useNavigate()

  const handleChange = (value: Options<Option> | SingleValue<Option>) => {
    if(Array.isArray(value)) {
      onChange({ target: { name, value: value.map(v => v.value).join(',') } } as any)
    } else if ((value as Option)?.value.startsWith('to:')) {
      return navigate((value as Option)?.value.replace('to:', ''))
    } else {
      return onChange({ target: { name, value: (value as Option)?.value } } as any)
    }
  }

  const getValue = (v: string | string[] | undefined): SingleValue<Option> | Options<Option> | undefined => {
    if(!v) return undefined
    if(Array.isArray(v)) {
      return options.filter(o => v.includes(String(o.value)))
    }
    return options?.find(o => String(o.value) === String(v)) as SingleValue<Option>
  }

  const isControlled = value !== undefined
  const selectedValue = isControlled ? getValue(value) : undefined
  const initialValue = !isControlled ? getValue(defaultValue) : undefined

  const selectTheme = (theme: any) => ({
    ...theme,
    borderRadius: 0,
    colors: {
      ...theme.colors,
      primary: '#0A146B',
      primary25: '#B7C6DA',
    },
  })
  const styles = {
    control: (baseStyles:any, state: any) => ({
      ...baseStyles,
      borderRadius: '5px',
      ...(noBorder ? { border: 'none', boxShadow: 'none' } : {}),
      ...(state.isDisabled ? { backgroundColor: '#fff', opacity: 1 } : {}),
    }),
    singleValue: (baseStyles: any, state: any) => ({
      ...baseStyles,
      ...(state.isDisabled ? { color: 'inherit' } : {}),
    }),
    indicatorSeparator: (baseStyles: any) => ({
      ...baseStyles,
      ...(noBorder ? { display: 'none' } : {}),
    }),
    dropdownIndicator: (baseStyles: any, state: any) => ({
      ...baseStyles,
      ...(state.isDisabled ? { display: 'none' } : {}),
    }),
  }
  return (
    <>
      {label && <FieldLabel label={label} htmlFor={id} size={size} required={required} />}
      <Select
        required={required}
        placeholder={placeHolder}
        styles={styles}
        theme={selectTheme}
        options={options}
        isMulti={isMulti}
        onChange={handleChange}
        {...(isControlled ? { value: selectedValue ?? null } : { defaultValue: initialValue })}
        isDisabled={disabled}
        isClearable={isClearable}
        {...rest}
      />
    </>
  )
}

export default SelectBox
